import { Head, router } from '@inertiajs/react'
import { useState } from 'react'
import DashboardLayout from '@/components/DashboardLayout'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { RefreshCw } from 'lucide-react'

interface GmailLabel {
  id: number
  labelId: string
  name: string
  createdAt: string
  updatedAt: string
}

interface IndexProps {
  account: {
    id: number
    name: string
  }
  gmailLabels: GmailLabel[]
  currentUser: {
    name: string
    email: string
    avatarUrl?: string
    timezone?: string
  }
  gmailConnection: {
    id: number
    email: string
    syncStatus: string
    active: boolean
  } | null
}

export default function Index({
  account,
  gmailLabels,
  currentUser,
  gmailConnection,
}: IndexProps) {
  const [syncing, setSyncing] = useState(false)

  const handleSync = async () => {
    setSyncing(true)
    router.post(`/accounts/${account.id}/gmail_labels/sync`, {}, {
      onFinish: () => setSyncing(false),
    })
  }

  return (
    <>
      <Head title="Gmail Labels" />
      <DashboardLayout
        currentUser={currentUser}
        gmailConnection={gmailConnection}
        account={account}
        activeTab="settings"
      >
        <div className="space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-blue-950 dark:text-white">
                Gmail Labels
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                All {gmailLabels.length} of your custom labels in your Gmail account.
              </p>
            </div>
            <Button
              onClick={handleSync}
              disabled={syncing || !gmailConnection}
              className="w-full sm:w-auto"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync Labels'}
            </Button>
          </div>

          {/* Labels List */}
          <Card className="bg-white overflow-hidden">
            <CardContent className="p-0">
              {gmailLabels.length > 0 ? (
                <div>
                  <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="hidden md:table-header-group bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Label Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Label ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Last Synced
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                      {gmailLabels.map((label) => (
                        <tr key={label.id} className="block md:table-row">
                          <td className="block md:table-cell px-4 py-3 md:px-6 md:py-4 text-sm font-medium text-blue-950 dark:text-white md:whitespace-nowrap">
                            <span className="inline-block md:hidden text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mr-2">
                              Label Name:
                            </span>
                            {label.name}
                          </td>
                          <td className="block md:table-cell px-4 py-3 md:px-6 md:py-4 text-sm text-gray-500 dark:text-gray-400 break-all md:whitespace-nowrap">
                            <span className="inline-block md:hidden text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mr-2">
                              Label ID:
                            </span>
                            {label.labelId}
                          </td>
                          <td className="block md:table-cell px-4 py-3 pb-6 md:px-6 md:py-4 md:pb-4 text-sm text-gray-500 dark:text-gray-400 break-all md:whitespace-nowrap">
                            <span className="inline-block md:hidden text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mr-2">
                              Last Synced:
                            </span>
                            {new Date(label.updatedAt).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 px-6">
                  <p className="text-gray-500 dark:text-gray-400 mb-4">
                    No Gmail labels found
                  </p>
                  <p className="text-sm text-gray-400 dark:text-gray-500">
                    Click "Sync Labels" to fetch your labels from Gmail
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    </>
  )
}
